import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Loader2, Wand2, ChevronDown, ChevronUp, Sparkles, Eye, X, Send, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  completed: 'text-slate-400 border-slate-700/50',
  in_progress: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/5',
  future: 'text-slate-500 border-slate-800/60',
};

const SEED_STATUS_COLORS = {
  planted: 'text-amber-300',
  hinted: 'text-amber-200',
  paid_off: 'text-emerald-400 line-through opacity-70',
};

export function StoryArcTab({ campaignId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showPlanner, setShowPlanner] = useState(false);

  const load = async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/arc-plan`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error('Arc fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [campaignId]);

  const arc = data?.arc_plan;
  const foreshadowing = data?.foreshadowing;
  const sceneDirector = data?.scene_director;
  const hasArc = arc?.acts && arc.acts.length > 0;

  return (
    <div className="space-y-4" data-testid="story-arc-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-fuchsia-300 font-mono flex items-center gap-2">
          <Wand2 className="h-4 w-4" /> Story Arc
        </h3>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={load}
            disabled={loading}
            className="h-7 px-2 text-xs text-slate-400 hover:text-white"
            data-testid="arc-refresh-btn"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            size="sm"
            onClick={() => setShowPlanner(true)}
            className="h-7 px-3 text-xs bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
            data-testid="arc-plan-btn"
          >
            <Sparkles className="h-3 w-3 mr-1" />
            {hasArc ? 'Replan Arc' : 'Plan Arc'}
          </Button>
        </div>
      </div>

      {loading && !data && (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && !hasArc && (
        <div className="rounded-lg border border-slate-700/50 bg-slate-900/40 p-4 text-sm text-slate-400">
          <p className="mb-2">No arc plan yet for this campaign.</p>
          <p className="text-xs text-slate-500">
            Click <span className="text-fuchsia-300 font-semibold">Plan Arc</span> to discuss the direction with an LLM and lock in a 5-act outline. The GM will use it as soft guidance — never as a railroad.
          </p>
        </div>
      )}

      {hasArc && (
        <>
          {/* Premise + themes */}
          <div className="rounded-lg border border-fuchsia-700/40 bg-fuchsia-950/20 p-3 text-sm space-y-2">
            {arc.premise && (
              <p className="text-slate-200 italic">"{arc.premise}"</p>
            )}
            {arc.themes && arc.themes.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {arc.themes.map((t) => (
                  <span key={t} className="px-2 py-0.5 text-xs rounded-full bg-fuchsia-500/10 text-fuchsia-300 border border-fuchsia-700/40">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Acts */}
          <div className="space-y-2">
            {arc.acts.map((act) => {
              const isOpen = !!expanded[act.number];
              const isCurrent = act.number === arc.current_act;
              const status = isCurrent ? 'in_progress' : act.status || (act.number < arc.current_act ? 'completed' : 'future');
              return (
                <div
                  key={act.number}
                  className={`rounded-lg border ${STATUS_COLORS[status] || STATUS_COLORS.future} bg-slate-950/30`}
                  data-testid={`arc-act-${act.number}`}
                >
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [act.number]: !p[act.number] }))}
                    className="w-full flex items-center justify-between p-3 text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-mono text-slate-500 flex-shrink-0">ACT {act.number}</span>
                      <span className="font-semibold truncate">{act.title || 'Untitled'}</span>
                      {isCurrent && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-500/20 text-emerald-300 font-mono flex-shrink-0">NOW</span>
                      )}
                    </div>
                    {isOpen ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3 space-y-2 text-sm text-slate-300">
                      {act.summary && <p>{act.summary}</p>}
                      {act.key_beats && act.key_beats.length > 0 && (
                        <ul className="list-disc list-inside text-xs text-slate-400 space-y-0.5">
                          {act.key_beats.map((b, i) => (<li key={i}>{b}</li>))}
                        </ul>
                      )}
                      {!isCurrent && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await axios.post(
                                `${API}/campaigns/${campaignId}/arc-plan/set-current-act`,
                                { act_number: act.number },
                                { withCredentials: true }
                              );
                              setData((prev) => ({ ...(prev || {}), arc_plan: res.data.arc_plan }));
                              toast.success(`Now in Act ${act.number}: ${act.title || ''}`);
                            } catch (err) {
                              toast.error('Failed to set act: ' + (err.response?.data?.detail || err.message));
                            }
                          }}
                          className="h-6 px-2 text-[11px] border-emerald-700/40 text-emerald-300 hover:bg-emerald-500/10"
                          data-testid={`arc-here-now-btn-${act.number}`}
                        >
                          Here Now
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Scene Director */}
          {sceneDirector?.upcoming_beats?.length > 0 && (
            <div className="rounded-lg border border-cyan-700/40 bg-cyan-950/20 p-3" data-testid="scene-director-box">
              <h4 className="text-xs font-mono uppercase tracking-wider text-cyan-300 mb-2 flex items-center gap-1">
                <Eye className="h-3 w-3" /> Scene Director
              </h4>
              <ul className="text-sm text-slate-300 space-y-1">
                {sceneDirector.upcoming_beats.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-cyan-400 flex-shrink-0">›</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Foreshadowing */}
          {foreshadowing?.seeds?.length > 0 && (
            <div className="rounded-lg border border-amber-700/40 bg-amber-950/10 p-3" data-testid="foreshadowing-box">
              <h4 className="text-xs font-mono uppercase tracking-wider text-amber-300 mb-2">
                Foreshadowing Seeds
              </h4>
              <ul className="text-xs space-y-1">
                {foreshadowing.seeds.map((s) => (
                  <li key={s.seed_id} className={`flex gap-2 ${SEED_STATUS_COLORS[s.status] || ''}`}>
                    <span className="font-mono text-[10px] uppercase opacity-60 flex-shrink-0 w-12">{s.status}</span>
                    <span className="flex-1">{s.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {showPlanner && (
        <ArcPlannerModal
          campaignId={campaignId}
          existingArc={arc}
          onClose={() => setShowPlanner(false)}
          onCommitted={async () => {
            setShowPlanner(false);
            await load();
            toast.success('Arc plan saved');
          }}
        />
      )}
    </div>
  );
}

function ArcPlannerModal({ campaignId, existingArc, onClose, onCommitted }) {
  // Persist the conversation per-campaign so an accidental close, refresh,
  // or commit failure doesn't wipe the planning work.
  const storageKey = `arcPlannerConvo:${campaignId}`;
  const initialMessages = (() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_) {}
    return [
      {
        role: 'assistant',
        content: existingArc?.acts?.length
          ? `Let's reshape your campaign arc. The current premise is: "${existingArc.premise || 'unset'}". What's not working, or what direction do you want to push toward?`
          : `Let's design your campaign arc. Tell me — what's the central question or conflict you want this story to wrestle with?`,
      },
    ];
  })();

  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [committing, setCommitting] = useState(false);
  const scrollRef = useRef(null);

  // Persist on every change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages));
    } catch (_) {}
  }, [messages, storageKey]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const newHistory = [...messages, { role: 'user', content: text }];
    setMessages(newHistory);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/campaigns/${campaignId}/arc-plan/discuss`,
        { history: messages, message: text },
        { withCredentials: true }
      );
      const reply = res.data?.reply || '(no reply)';
      setMessages([...newHistory, { role: 'assistant', content: reply }]);
    } catch (e) {
      toast.error('Planner error: ' + (e.response?.data?.detail || e.message));
      setMessages(newHistory);
    } finally {
      setSending(false);
    }
  };

  const resetConversation = () => {
    try { localStorage.removeItem(storageKey); } catch (_) {}
    setMessages([
      {
        role: 'assistant',
        content: existingArc?.acts?.length
          ? `Fresh start. The current premise is: "${existingArc.premise || 'unset'}". What's not working, or what direction do you want to push toward?`
          : `Fresh start. What's the central question or conflict you want this story to wrestle with?`,
      },
    ]);
  };

  const commit = async () => {
    setCommitting(true);
    try {
      // Take the last user turn (or full thread) as guidance
      const lastUser = [...messages].reverse().find((m) => m.role === 'user');
      const guidance = lastUser?.content || 'Use the conversation as guidance.';
      const res = await axios.post(
        `${API}/campaigns/${campaignId}/arc-plan/commit`,
        { user_guidance: guidance, history: messages },
        { withCredentials: true }
      );
      const acts = res.data?.arc_plan?.acts;
      if (!acts || acts.length === 0) {
        toast.error('Generator returned no acts. Try giving more specific guidance, then commit again.');
        return; // keep modal + conversation
      }
      // Success — clear stored conversation
      try { localStorage.removeItem(storageKey); } catch (_) {}
      onCommitted();
    } catch (e) {
      toast.error('Commit failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6" data-testid="arc-planner-modal">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl border border-fuchsia-700/40 bg-slate-950 shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h3 className="text-base font-bold text-fuchsia-300 font-mono flex items-center gap-2">
            <Wand2 className="h-4 w-4" /> Arc Planner
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="arc-planner-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 ${
                  m.role === 'user'
                    ? 'bg-fuchsia-600/30 text-fuchsia-50 border border-fuchsia-700/40'
                    : 'bg-slate-900 text-slate-200 border border-slate-700/60'
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-slate-900 border border-slate-700/60 rounded-lg px-3 py-2 text-slate-400 text-xs flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-800 space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder="Tell the planner what you want…"
              className="flex-1 bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-fuchsia-500"
              disabled={sending || committing}
              data-testid="arc-planner-input"
            />
            <Button
              size="sm"
              onClick={send}
              disabled={!input.trim() || sending || committing}
              className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
              data-testid="arc-planner-send-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <Button
            onClick={commit}
            disabled={committing || messages.length < 2}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm"
            data-testid="arc-planner-commit-btn"
          >
            {committing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating arc…</>) : 'Commit & Generate Arc Plan'}
          </Button>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={resetConversation}
              className="text-[10px] text-slate-500 hover:text-slate-300 underline"
              data-testid="arc-planner-reset-btn"
            >
              Reset conversation
            </button>
            <p className="text-[10px] text-slate-500">
              Commit when ready. The planner will produce a 5-act outline using your conversation as guidance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
